import type { DistributionPlannerTranslations } from "../en/DistributionPlanner";
import type { TranslationShape } from "../../store/types";

export const distributionPlannerEs: TranslationShape<DistributionPlannerTranslations> = {
  pageTitle: "Planificador de Distribución",
  pageSubtitle:
    "Revisa en cuántas tiendas está cada SKU, define objetivos mínimos y máximos de tiendas y reequilibra la distribución en la red",

  category: {
    title: "Categoría",
    placeholder: "categoría",
    prompt: "Selecciona una categoría para empezar a planificar la distribución",
    loading: "Cargando categorías...",
    loadFailed: "No se pudieron cargar las categorías.",
    invalid: "Categoría inválida.",
  },

  status: {
    loading: "Cargando distribución...",
    loadError: "No se pudo cargar el plan de distribución.",
  },

  actions: {
    saveTargets: "Guardar objetivos",
    runDistribution: "Ejecutar distribución",
  },

  readOnly: {
    hint: "Vista de solo lectura: puedes filtrar y exportar este plan. Editar objetivos y ejecutar la distribución requieren el permiso de actualización de inputs.",
  },

  categoriesEntry: {
    button: "Distribución",
  },

  priority: {
    label: "Prioridad de la ejecución:",
    core: "Core range",
    minMax: "Objetivos mín/máx de tiendas",
    spaceMix: "Objetivos de mezcla de espacio",
    hint: "Los pasos anteriores ganan cuando las restricciones chocan: el core range se coloca primero y nunca se quita, los mín/máx de tiendas aplican después, y los objetivos de mezcla solo dirigen el espacio que sobra.",
  },

  tiles: {
    storeFleet: "Red de tiendas",
    storeFleetHint: "Tiendas en esta categoría",
    masterList: "Lista maestra",
    masterListHint: "Productos en alcance",
    withinTarget: "Dentro del objetivo",
    belowMin: "Bajo el mínimo",
    aboveMax: "Sobre el máximo",
    noTarget: "Sin objetivo",
  },

  spaceMix: {
    title: "Mezcla de espacio",
    subtitle:
      "Participación actual de las ventas de la categoría por segmento, frente a la mezcla que quieres planificar. Los objetivos se guardan con la categoría y anulan la próxima ejecución de distribución; el selector de abajo decide si un objetivo es un total de toda la red o la misma participación en cada tienda. Los segmentos sin objetivo siguen la mezcla de ventas propia de cada tienda.",
    mode: {
      label: "Cómo se aplican los objetivos",
      network: "Total de red, inclinado por tienda",
      uniform: "Participación uniforme en cada tienda",
      explainer:
        "Total de red: el objetivo es una participación del espacio de toda la red. La ejecución inclina la mezcla de ventas propia de cada tienda hacia arriba o abajo hasta que el total de todas las tiendas alcanza el objetivo, así que las diferencias de demanda entre tiendas sobreviven. Uniforme: cada tienda se planifica exactamente con la participación objetivo — una mezcla idéntica en toda la red, a costa de las diferencias de demanda locales. No se admiten objetivos por clúster.",
    },
    segment: "Segmento",
    skus: "{{count}} SKUs",
    currentShare: "Participación actual",
    currentShareExplainer:
      "La participación actual de un segmento es su retorno de espacio basado en ventas: cuánto de las ventas de la categoría aportan los SKUs del segmento.",
    equationLhs: "Participación actual (%)",
    equationNumerator: "Σ ventas de los SKUs del segmento en todas las tiendas",
    equationDenominator: "Σ ventas de todos los SKUs de la categoría en todas las tiendas",
    equationNote:
      "Las ventas se suman en todas las tiendas, esté o no surtido el SKU actualmente — la participación mide la demanda y no cambia cuando cambia la distribución.",
    targetShare: "Objetivo %",
    targetShareExplainerUniform:
      "Los objetivos se aplican de manera uniforme: cada tienda se planifica con la misma participación del segmento, reemplazando la mezcla de ventas propia de esa tienda. Una tienda que sobre- o sub-indexa en el segmento recibe el mismo objetivo, así que trátalo como una anulación deliberada para toda la cadena y no como un pronóstico de demanda. Los límites mín/máx de tiendas por SKU siguen aplicando, por lo que las participaciones realizadas variarán tienda a tienda.",
    targetShareExplainerNetwork:
      "Los objetivos se aplican como un total de red: la ejecución inclina la mezcla de ventas propia de cada tienda por un factor común hasta que el total ponderado por capacidad de todas las tiendas alcanza el objetivo. Las tiendas que sobre-indexan en el segmento quedan por encima y las que sub-indexan quedan por debajo — solo el total de la red aterriza en el número. Los límites mín/máx de tiendas por SKU siguen aplicando.",
    delta: "Diferencia",
    onTarget: "En objetivo",
    points: "{{value}} pt",
    totalAllocated: "Total asignado",
    ofHundred: "de 100%",
    reset: "Restablecer a la mezcla actual",
    reservedNote:
      "Los objetivos mínimos de tiendas, los SKUs de core range y la cobertura obligatoria por tienda reservan ~{{reserved}}% del espacio total — los objetivos de mezcla solo pueden dirigir el ~{{free}}% restante.",
    reservedExplainer:
      "Un objetivo mínimo de tiendas es un número de tiendas, pero cada una de esas tiendas debe darle al SKU espacio físico: su ancho en el estante × un promedio de frentes definido por el sistema. Los SKUs de core range cuentan igual — cada uno es una colocación garantizada en su tienda, surtida antes que todo lo demás. Además, cada tienda debe llevar al menos un SKU de cada segmento, así que por tienda también se reserva el SKU elegible más estrecho de cada segmento — o los SKUs core ya colocados allí. El piso de un segmento es el mayor entre sus mínimos y core y esa cobertura; sumando los pisos, dividido por la capacidad total de muebles de la red, se obtiene la participación reservada. Es un agregado de toda la red — tiendas individuales pueden estar comprometidas más o menos que esto.",
    unreachableTarget:
      "Objetivo inalcanzable: los límites máximos de tiendas y el espacio que reservan los mínimos, el core range y la cobertura de otros segmentos limitan a {{segment}} a aproximadamente {{max}}% del espacio.",
    forcedLowTarget:
      "Ya por encima del objetivo: los mínimos, el core range y la cobertura por tienda de {{segment}} reservan aproximadamente {{min}}% del espacio, así que la ejecución no puede bajar de ahí.",
    noData: "No hay segmentos para mostrar en esta categoría.",
    unassigned: "Sin asignar",
    automaticLabel: "Auto",
    automaticTarget:
      "Automático: sin objetivo. Este segmento toma su parte de lo que dejan los objetivos explícitos, siguiendo las ventas actuales.",
    feasibilityStale:
      "La factibilidad refleja los últimos objetivos mín/máx guardados — {{count}} edición(es) de productos sin guardar no se incluyen hasta que guardes.",
    allocation: {
      automatic:
        "Sin objetivos explícitos: el 100% es automático, según la demanda de cada tienda.",
      over: "Los objetivos explícitos suman {{total}}% — más de 100%. La ejecución los reduce todos proporcionalmente (sus tamaños relativos sobreviven) y los segmentos sin objetivo no reciben participación propia.",
      underComplete:
        "Todos los segmentos tienen objetivo explícito pero suman {{total}}% — menos de 100%. El sobrante no tiene a dónde ir, así que la ejecución escala los objetivos proporcionalmente hasta llenar el espacio.",
      underPartial:
        "Los objetivos explícitos suman {{total}}%. El {{rest}}% restante fluye a los {{count}} segmento(s) sin objetivo, siguiendo la mezcla de ventas propia de cada tienda.",
    },
    clusters: {
      networkTab: "Todos los clústeres",
      networkHint: "total de red",
      storeCount: "{{count}} tiendas",
      unclustered: "Sin clúster",
      targetShare: "Objetivo %",
      clusterShare: "Participación del clúster",
      simInput: "Simular ventas (± pt)",
      adjustedShare: "Participación ajustada",
      networkNote:
        "Mientras hay una simulación activa, el objetivo es la suma ponderada por ventas de tu mezcla simulada por clúster; si no, muestra los objetivos guardados o la participación actual donde no hay ninguno. Abre una pestaña de clúster para simular ventas de segmento actualizadas; al guardar se persiste esa suma como objetivos.",
      clusterNote:
        "Elige + o − e ingresa puntos porcentuales para simular las ventas actualizadas de este segmento en este clúster. Los segmentos ajustados mantienen su nueva participación y el resto del clúster se reequilibra al 100%. Al guardar se persiste el objetivo de red resultante.",
    },
  },

  scatter: {
    title: "Distribución vs ventas por tienda",
    subtitle:
      "Cada punto es un SKU del filtro actual. Las líneas de referencia marcan la mediana de los SKUs visibles.",
    xAxis: "Tiendas con el SKU",
    yAxis: "Venta promedio / tienda",
    hintExpand: "alta venta/tienda, baja distribución — ¿ampliar?",
    hintCut: "baja venta/tienda, alta distribución — ¿reducir?",
    tooltipDistribution: "Distribución",
    tooltipAvgSales: "Venta promedio / tienda",
    tooltipStores: "{{count}} tiendas",
    noData: "Ningún SKU coincide con los filtros actuales.",
    legendNoTarget: "Sin objetivo",
    legendOk: "Dentro del objetivo",
    legendBelow: "Bajo el mínimo",
    legendAbove: "Sobre el máximo",
  },

  table: {
    title: "Productos",
    searchPlaceholder: "Buscar nombre, marca o código de producto",
    allOf: "Todos: {{name}}",
    coreAll: "Todos los SKUs",
    coreOnly: "Solo core range",
    coreNon: "Solo no core",
    statusAll: "Todos los estados",
    statusNone: "Sin objetivo",
    statusOk: "Dentro del objetivo",
    statusBelow: "Bajo el mínimo",
    statusAbove: "Sobre el máximo",
    results: "{{count}} de {{total}} SKUs",
    dirtyHint: "{{count}} cambios de mín/máx sin guardar",
    loading: "Cargando productos...",
    noRows: "Ningún producto coincide con los filtros actuales.",
    yes: "Sí",
    none: "—",
    exportCsv: "Exportar CSV",
    importCsv: "Importar CSV",
    importCsvHint: "Importar objetivos mín/máx por código de producto",
  },

  columns: {
    product: "Producto",
    core: "Core",
    avgSales: "Venta prom./tienda",
    distribution: "Distribución",
    min: "Mín",
    max: "Máx",
    status: "Estado",
  },

  statusLabels: {
    ok: "Dentro del objetivo",
    below: "Bajo el mínimo",
    above: "Sobre el máximo",
    none: "Sin objetivo",
  },

  bulk: {
    hint: "Marca las casillas de la primera columna (o la casilla del encabezado) para definir objetivos mín/máx de tiendas de varios SKUs a la vez.",
    selected: "{{count}} SKUs seleccionados",
    min: "Definir mínimo",
    max: "Definir máximo",
    setMin: "Definir mínimo",
    setMax: "Definir máximo",
    clearMinMax: "Limpiar mín/máx",
    clearSelection: "Limpiar selección",
  },

  run: {
    title: "Ejecutar distribución",
    subtitle: "Recalcula los surtidos de tienda de esta categoría",
    explanation:
      "Recalcula el surtido de tiendas de cada SKU de esta categoría. Los SKUs de core range se colocan primero y nunca se quitan; después los objetivos mín/máx de cada SKU, la capacidad del mueble, la cobertura de variantes y tus objetivos de mezcla de espacio guardados dirigen el resto — los objetivos se aplican según tu modo guardado: uniformemente en cada tienda, o como total de red inclinado por tienda. Esto reescribe surtidos de tienda en vivo.",
    skus: "SKUs",
    storeFleet: "Red de tiendas",
    withTargets: "Con mín/máx definido",
    warningTitle: "Esto modifica surtidos de tienda en vivo",
    warningBody:
      "El número de tiendas lo decide la ejecución, no se ingresa aquí. Se reasignan todos los SKUs de la categoría, así que la distribución puede subir o bajar en cualquiera de ellos.",
    savesDirtyTargets:
      "{{count}} ediciones de mín/máx sin guardar se guardarán primero, porque la ejecución lee los objetivos persistidos.",
    savesDirtyMix:
      "Los objetivos de mezcla de espacio sin guardar se guardarán primero — la ejecución los usa como anulaciones por segmento.",
    running: "Ejecutando la asignación: puede tardar un minuto. Mantén esta ventana abierta.",
    mixOverAllocated:
      "Los objetivos de mezcla guardados suman {{total}}% — la ejecución los reduce proporcionalmente a 100%.",
    mixUnderAllocated:
      "Todos los segmentos tienen objetivo de mezcla pero suman {{total}}% — la ejecución los escala hasta llenar el espacio.",
    cancel: "Cancelar",
    confirm: "Ejecutar distribución",
  },

  mixConfirm: {
    title: "Los objetivos de mezcla suman {{total}}%",
    subtitle: "La ejecución no aplicará estos números tal cual",
    over: "Tus objetivos explícitos suman {{total}}%, más de 100%. Guardar está bien — pero la ejecución de distribución reduce cada objetivo por el mismo factor para que quepan en 100%. Los tamaños relativos sobreviven; los números absolutos que escribiste no, y los segmentos sin objetivo no reciben participación propia.",
    underComplete:
      "Todos los segmentos tienen objetivo explícito, pero juntos solo suman {{total}}%. El espacio sobrante no tiene a dónde ir, así que la ejecución escala tus objetivos proporcionalmente hasta llenar el 100%. Los tamaños relativos sobreviven; los números absolutos que escribiste no.",
    hint: "Para que los números se apliquen tal cual, ajústalos para que sumen 100% — o deja al menos un segmento sin objetivo para absorber la diferencia.",
    cancel: "Volver a editar",
    confirm: "Guardar de todos modos",
  },

  runResult: {
    title: "Distribución completada",
    subtitle: "Terminó en {{seconds}}s. Los surtidos quedaron actualizados.",
    changedSkus: "SKUs modificados",
    added: "Tiendas agregadas",
    removed: "Tiendas quitadas",
    rangedNote:
      "{{rangedRows}} filas tienda/SKU surtidas en {{products}} SKUs y {{stores}} tiendas después de la ejecución.",
    warningsTitle: "{{count}} advertencias",
    noWarnings: "Sin advertencias — todas las restricciones se cumplieron.",
    close: "Cerrar",
  },

  toasts: {
    targetsSaved: "Objetivos guardados",
    targetsSavedMessage: "{{count}} productos actualizados.",
    targetsSaveFailed: "No se pudieron guardar los objetivos",
    spaceMixSavedMessage: "{{count}} objetivos de segmento actualizados.",
    spaceMixSaveFailed: "No se pudieron guardar los objetivos de mezcla de espacio",
    nothingToSave: "No hay cambios para guardar",
    runFailed: "No se pudo ejecutar la distribución",
    exportEmpty: "No hay nada para exportar",
    exportEmptyMessage: "Ningún producto coincide con los filtros actuales.",
    imported: "CSV importado",
    importSummary:
      "{{count}} productos actualizados, {{unknown}} códigos de producto desconocidos, {{invalid}} filas inválidas. Revisa las filas resaltadas y guarda los objetivos.",
    importFailed: "No se pudo importar el CSV",
    importNoKeyColumn: "El archivo necesita una columna product_code.",
  },
};
